/**
 * Login state hook
 * Extracted from useLoginController.ts to reduce file size
 */

import { useState, type Dispatch, type SetStateAction } from "react";
import { ValidationError } from "class-validator";
import { DeliveryChannel } from "@Front/Api/Auth/Customer/Auth";
import type { CustomerLoginCandidateDto, EmailAuthCandidatesResponse } from "@Front/Api/Public/emailAuthCandidatesApi";
import type { CustomerAuthDisambiguation } from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import { LoginStep } from "../loginSteps";

export type CustomerLoginState = {
	step: LoginStep;
	setStep: Dispatch<SetStateAction<LoginStep>>;
	totpCodeUid: string;
	setTotpCodeUid: (uid: string) => void;
	totpCode: string;
	setTotpCode: (code: string) => void;
	email: string;
	setEmail: (email: string) => void;
	partialPhoneNumber: string;
	setPartialPhoneNumber: (value: string) => void;
	deliveryChannel: DeliveryChannel;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	maskedEmail: string;
	setMaskedEmail: (email: string) => void;
	validationErrors: ValidationError[];
	setValidationErrors: (errors: ValidationError[]) => void;
	loginCandidates: EmailAuthCandidatesResponse | null;
	setLoginCandidates: Dispatch<SetStateAction<EmailAuthCandidatesResponse | null>>;
	authDisambiguation: CustomerAuthDisambiguation;
	setAuthDisambiguation: Dispatch<SetStateAction<CustomerAuthDisambiguation>>;
	/** Sous-ensemble affiché pour CUSTOMER_TARGET_CHOICE (après filtre étude ou lien étroit). */
	customerTargetChoiceCandidates: CustomerLoginCandidateDto[] | null;
	setCustomerTargetChoiceCandidates: Dispatch<SetStateAction<CustomerLoginCandidateDto[] | null>>;
};

/**
 * Hook for managing login state
 */
export function useLoginState(): CustomerLoginState {
	const [step, setStep] = useState<LoginStep>(LoginStep.EMAIL);
	const [totpCodeUid, setTotpCodeUid] = useState<string>("");
	const [totpCode, setTotpCode] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [partialPhoneNumber, setPartialPhoneNumber] = useState<string>("");
	const [deliveryChannel, setDeliveryChannel] = useState<DeliveryChannel>("sms");
	const [maskedEmail, setMaskedEmail] = useState<string>("");
	const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
	const [loginCandidates, setLoginCandidates] = useState<EmailAuthCandidatesResponse | null>(null);
	const [authDisambiguation, setAuthDisambiguation] = useState<CustomerAuthDisambiguation>({});
	const [customerTargetChoiceCandidates, setCustomerTargetChoiceCandidates] = useState<CustomerLoginCandidateDto[] | null>(null);

	return {
		step,
		setStep,
		totpCodeUid,
		setTotpCodeUid,
		totpCode,
		setTotpCode,
		email,
		setEmail,
		partialPhoneNumber,
		setPartialPhoneNumber,
		deliveryChannel,
		setDeliveryChannel,
		maskedEmail,
		setMaskedEmail,
		validationErrors,
		setValidationErrors,
		loginCandidates,
		setLoginCandidates,
		authDisambiguation,
		setAuthDisambiguation,
		customerTargetChoiceCandidates,
		setCustomerTargetChoiceCandidates,
	};
}

